import { IPromise, IScope } from "angular";
import { StateService } from "@uirouter/angular";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IStateInit } from "@gtmhub/core/routing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { createTeamsTree } from "@gtmhub/shared/tree-renderer/utils";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState, TeamService, TeamsActions, addAssigneeDataToTeams } from "@gtmhub/teams";
import { OrganizationTreeRenderer } from "@gtmhub/teams/utils/organization-tree-renderer";
import { IdMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { confirmDelete } from "@webapp/shared/modal/confirm";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { addAssigneeDataToTeamsMap } from "../redux/teams-actions";
import OrganizationTreeNode from "../utils/organization-tree-node";

type OrganizationChartIndicators = {
  loading?: IIndicator & { solid: boolean };
};

export interface IOrganizationTreeScope extends IScope, IExtendedRootScopeService, IOrganizationRedux {
  collapseOrganizationTree(): void;
  expandOrganizationTree(): void;

  toggleSomeTeamMembersOption(): void;

  switchDragMode(): void;

  confirmDeleteTeam(teamId: string): void;
  handleRemoveTeamManager(teamId: string): IPromise<void>;
  handleRemoveTeamMember(teamId: string, memberId: string): IPromise<void>;

  resetTreeView(): void;

  contextMenuTeamId: string;
  contextMenuUserId: string;
  dragEl: HTMLElement;
  dragEnabled: boolean;
  draggedTeamId: string;
  indicators: OrganizationChartIndicators;

  showSomeTeamMembers: boolean;
  hasAccessPeople: boolean;
}

interface IOrganizationRedux {
  teamsIdMap: IdMap<ITeam>;
  teams: ITeam[];
  reduxItemsFetched: boolean;
  reduxError: IUIError;
  noTeamsInAccount: boolean;
}

const bindStateToScope = (state: ITeamsStoreState & IAssigneesStoreState): IOrganizationRedux => {
  return {
    teamsIdMap: addAssigneeDataToTeamsMap(state.teams.map, state.assignees.map),
    teams: addAssigneeDataToTeams(state.teams.items, state.assignees.map).sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1)),
    reduxItemsFetched: state.teams.isFetched && state.assignees.isFetched,
    reduxError: state.teams.error || state.assignees.error,
    noTeamsInAccount: state.teams.isFetched && state.teams.items.length === 0,
  };
};

export class OrganizationChartCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$ngRedux",
    "TeamsActions",
    "UIErrorHandlingService",
    "TeamService",
    "Ng1UiModalService",
    "AnalyticsService",
    "ApmSpanService",
  ];

  private pageTiming = 0;
  private subscriptions = [];
  private treeContainer: HTMLElement;
  private tree: OrganizationTreeRenderer;

  private animationDuration = location.hash.includes("animation=1") ? 330 : 0; // for non-technical people, to decide easily what's better
  private debug = location.hash.includes("timing=1"); // easily log loading and processing times on any environment (it's safe, no sensitive data is logged!)
  private initialDisplay = true;
  private defaultZoom = 1;
  private defaultTreePosition = { x: 200, y: 60 };
  private resetTreeAnimationDuration = location.hash.includes("animation=1") ? 500 : 0; // for non-technical people, to decide easily what's better

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IOrganizationTreeScope,
    private $state: StateService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private teamService: TeamService,
    private modalService: UiModalService,
    private analyticsService: AnalyticsService,
    private apmSpanService: ApmSpanService
  ) {
    // actions at the top
    this.$scope.collapseOrganizationTree = this.collapseOrganizationTree;
    this.$scope.expandOrganizationTree = this.expandOrganizationTree;
    this.$scope.toggleSomeTeamMembersOption = this.toggleSomeTeamMembersOption;
    this.$scope.switchDragMode = this.switchDragMode;
    this.$scope.confirmDeleteTeam = this.confirmDeleteTeam;
    this.$scope.handleRemoveTeamManager = this.handleRemoveTeamManager;
    this.$scope.handleRemoveTeamMember = this.handleRemoveTeamMember;
    this.$scope.resetTreeView = this.resetTreeView;

    OrganizationTreeNode.handleTeamNameClick = this.showTeam;
    OrganizationTreeNode.handleToggleChildren = this.toggleTeam;
    OrganizationTreeNode.handleManagerClick = this.showManager;
    OrganizationTreeNode.handleTeamMemberClick = this.showUser;
    OrganizationTreeNode.handleContextMenuClick = this.openContextMenu;
    OrganizationTreeNode.handleTeamDrag = this.onTeamDragStart;
    OrganizationTreeNode.handleTeamDrop = this.onTeamDrop;
    OrganizationTreeNode.handleHideTeamMembersClick = this.hideTeamMembers;
    OrganizationTreeNode.handleShowTeamMembersClick = this.showTeamMembers;

    this.$scope.contextMenuTeamId = "";
    this.$scope.contextMenuUserId = "";
    this.$scope.dragEnabled = false;
    this.$scope.draggedTeamId = "";
    this.$scope.indicators = {};
    this.$scope.showSomeTeamMembers = true;
    this.$scope.hasAccessPeople = this.$rootScope.hasPermission("AccessPeople");

    this.subscriptions.push(this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.$scope.$on("$destroy", () => this.stateClear());

    this.subscriptions.push(
      this.$scope.$watch(
        "teams",
        () => {
          if ((this.debug && !this.$scope.teams?.length) || !this.$scope.reduxItemsFetched || !this.$scope.hasAccessPeople) {
            return;
          }

          this.safeDisplayTree();
        },
        true
      )
    );
  }

  public stateInit(): IPromise<void> {
    if (this.$scope.hasAccessPeople) {
      try {
        this.treeContainer = document.querySelector("#organization-tree-holder") as HTMLElement;
        this.tree = new OrganizationTreeRenderer(this.treeContainer);
        this.configureTree();

        this.treeContainer.addEventListener("click", this.closeContextMenu);
      } catch (e) {
        this.uiErrorHandlingService.handleModal(e);
      }

      return this.apmSpanService.dataLoadSpan("org-chart-init", () => this.init());
    }
  }

  private stateClear(): void {
    this.subscriptions.forEach((un) => un());
    if (this.tree) {
      this.tree.destroy();
    }

    if (this.treeContainer) {
      this.treeContainer.removeEventListener("click", this.closeContextMenu);
    }
  }

  private init = () => {
    this.pageTiming = Date.now();

    this.showLoadingIndicator({ solid: true });
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxError
      )
      .then(
        () => {
          this.$scope.dragEl = document.getElementById("org-chart-drag-handle");

          // do not apply the animations at the very beginning to reduce CPU usage on initial load
          if (!this.treeContainer) {
            this.treeContainer = document.querySelector("#organization-tree-holder") as HTMLElement;
          }
          window.setTimeoutOutsideAngular(() => {
            this.treeContainer.style.setProperty("--animation-duration", `${this.animationDuration}ms`);
          }, 3000);
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
        }
      )
      .finally(() => this.hideLoadingIndicator());
  };

  private onInitialDisplay() {
    this.initialDisplay = false;

    // GVS-16583: on every start, hide team members for better performance
    this.$scope.teams.forEach(({ id }) => this.tree.collapseTeamMembers(id));

    // GVS-16583: on the very first start, display a folded tree to prevent performance issues
    if (!this.tree.isThereState()) {
      this.tree.foldAllNodes().saveState();
    }
  }

  private showLoadingIndicator({ solid }: { solid: boolean }): void {
    this.$scope.indicators = {
      loading: { progress: true, solid },
    };
  }

  private hideLoadingIndicator(): void {
    if (this.$scope.indicators.loading) {
      delete this.$scope.indicators.loading;
    }
  }

  private showTeam = (teamId: string) => {
    this.$state.go(".team", { teamId: teamId });
  };

  private showManager = (managerId: string, teamId: string) => {
    if (managerId) {
      this.showUser(managerId);
    } else {
      this.$state.go(".team", { teamId: teamId, focusSelectManagerFiled: true });
    }
  };

  private showUser = (userId: string) => {
    this.$state.go("gtmhub.organization_chart.employee", { employeeId: userId });
  };

  private toggleTeam = (teamId: string) => {
    try {
      this.tree.toggleFoldNode(teamId).saveState().render();
    } catch (e) {
      this.uiErrorHandlingService.handleModal(e);
    }
  };

  private toggleSomeTeamMembersOption = (): void => {
    const trackEventName = this.$scope.showSomeTeamMembers ? "Expand team members clicked" : "Collapse team members clicked";
    this.analyticsService.track(trackEventName);

    this.$scope.showSomeTeamMembers = !this.$scope.showSomeTeamMembers;
    this.tree.setOptions({ showSomeMembers: this.$scope.showSomeTeamMembers });

    if (!this.$scope.showSomeTeamMembers) {
      this.$scope.teams.forEach(({ id }) => this.tree.collapseTeamMembers(id));
    } else {
      this.renderTreeDelayed(this.animationDuration / 2); // 50% unfolded; recalculate for smoother visual experience
    }

    // recalculate all node dimensions, to avoid "holes" after members list has been collapsed
    this.renderTreeDelayed(this.animationDuration);
  };

  private hideTeamMembers = (teamId: string): void => {
    this.tree.collapseTeamMembers(teamId);
    // recalculate all node dimensions, to avoid "holes" after members list has been collapsed
    this.renderTreeDelayed(this.animationDuration);
  };

  private showTeamMembers = (teamId: string): void => {
    this.tree.expandTeamMembers(teamId).render();
  };

  private collapseOrganizationTree = (): void => {
    try {
      this.tree.foldAllNodes().saveState().render();
    } catch (e) {
      this.uiErrorHandlingService.handleModal(e);
    }
  };

  private expandOrganizationTree = (): void => {
    try {
      this.tree.unfoldAllNodes().saveState().render();
    } catch (e) {
      this.uiErrorHandlingService.handleModal(e);
    }
  };

  private openContextMenu = (event: Event, rowSelector: string, menuId: string, teamId: string, userId: string) => {
    const menu = document.getElementById(menuId);
    if (!menu) {
      return;
    }
    const itemsList = menu.querySelector(".gh-ui-actions-dd");
    if (!itemsList) {
      return;
    }

    if (!menu.classList.contains("hidden") && this.$scope.contextMenuTeamId === teamId) {
      return;
    }

    const parentRow = (event.target as HTMLElement).closest(rowSelector);
    if (!parentRow) {
      return;
    }

    this.closeContextMenu(); // close all others before opening this one

    this.$scope.contextMenuTeamId = teamId;
    this.$scope.contextMenuUserId = userId;
    this.$scope.$digest();

    parentRow.appendChild(menu);

    menu.classList.remove("hidden");
    itemsList.classList.add("open");
  };

  private closeContextMenu = (event?: Event) => {
    if (event && (event.target as HTMLElement).className.includes("gh-font-icon-menu")) {
      return;
    }

    Array.from(document.querySelectorAll(".context-menu")).forEach((menu) => {
      this.$scope.contextMenuTeamId = "";
      this.$scope.contextMenuUserId = "";

      // prevent accidental deletion of the menu, in case the tree deletes the node for some reason
      document.querySelector(".dropdown-actions-container").appendChild(menu);

      const itemsList = menu.querySelector(".gh-ui-actions-dd");
      if (!itemsList) {
        return;
      }

      menu.classList.add("hidden");
      itemsList.classList.remove("open");
    });
  };

  private switchDragMode = (): void => {
    this.$scope.dragEnabled = !this.$scope.dragEnabled;

    this.tree.setOptions({ isNodeDraggingEnabled: this.$scope.dragEnabled }).saveState();
  };

  private onTeamDragStart = (event: DragEvent, teamId: string, teamColor: string): void => {
    const draggedTeam = this.$scope.teamsIdMap[teamId];
    if (!draggedTeam) {
      this.uiErrorHandlingService.handleModal(new Error("Cannot drag a non-existing team."));
      return;
    }

    this.onTeamDrag(event);
    window.addEventListener("mousemove", this.onTeamDrag);
    window.addEventListener("mouseup", this.onTeamDragEnd);

    const forbiddenDropTargetIds = this.tree.getAllChildTeams(draggedTeam.id).map((team) => team.id);
    if (draggedTeam.parentId) {
      forbiddenDropTargetIds.push(draggedTeam.parentId);
    }
    forbiddenDropTargetIds.forEach((teamId) => {
      const target: HTMLElement = this.treeContainer.querySelector(`.drop-target-${teamId}`);
      if (target) {
        target.classList.add("hidden");
      }
    });

    (event.target as HTMLElement).classList.add("current-team-move-sign");
    this.$scope.dragEl.style.backgroundColor = teamColor; // sometimes backend does not send color data, so we use whatever the Node has filled in
    this.$scope.draggedTeamId = draggedTeam.id;
    this.$scope.$digest();
  };

  private onTeamDrag = (event: MouseEvent): void => {
    const x = event.x - 10;
    const y = event.y - 10;
    const zoom = this.tree.getOptions().zoom;
    this.$scope.dragEl.style.transform = `scale3d(${zoom}, ${zoom}, 1) translate3d(${x / zoom}px, ${y / zoom}px, 0)`;
    this.$scope.$digest();
  };

  private onTeamDragEnd = (): void => {
    window.removeEventListener("mousemove", this.onTeamDrag);
    window.removeEventListener("mouseup", this.onTeamDragEnd);

    Array.from(this.treeContainer.querySelectorAll(".current-team-move-sign")).forEach((e: HTMLElement) => e.classList.remove("current-team-move-sign"));
    Array.from(this.treeContainer.querySelectorAll(".drop-handle")).forEach((e: HTMLElement) => e.classList.remove("hidden"));
    this.$scope.draggedTeamId = "";
    this.$scope.$digest();
  };

  private onTeamDrop = (droppedTeamId: string): void => {
    this.showLoadingIndicator({ solid: false });
    this.teamService
      .updateTeam({ id: this.$scope.draggedTeamId, parentId: droppedTeamId })
      .then(
        (res) => {
          this.$ngRedux.dispatch(this.teamsActions.updateTeam(res));
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      )
      .finally(() => this.hideLoadingIndicator());
  };

  private confirmDeleteTeam = (teamId: string): void => {
    confirmDelete(this.modalService, {
      title: localize("delete_team_popup_title"),
      description: localize("delete_team_popup_description"),
    }).subscribe(() => {
      this.handleDeleteTeam(teamId);
    });
  };

  private handleDeleteTeam(teamId: string): IPromise<void> {
    const team = this.$scope.teamsIdMap[teamId];
    if (!team) {
      this.uiErrorHandlingService.handleModal(new Error("Cannot delete a non-existing team."));
      return Promise.resolve();
    }

    const parentId = team.parentId;

    return this.teamService.deleteTeam(teamId).then(
      () => {
        this.$scope.teams.forEach((t) => {
          // move any orphaned teams under their parent
          if (t.parentId === teamId) {
            t.parentId = parentId || "root";
          }
        });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private handleRemoveTeamManager = (teamId: string): IPromise<void> => {
    return this.teamService.removeTeamManager(teamId).then(
      () => null, // then => nothing. If any other component ever needs to know, here is the place to broadcast an event.
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private handleRemoveTeamMember = (teamId: string, memberId: string): IPromise<void> => {
    return this.teamService.removeTeamMember(teamId, memberId).then(
      () => null, // then => nothing. If any other component ever needs to know, here is the place to broadcast an event.
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private configureTree() {
    this.tree.setOptions({
      debug: this.debug,
      hasManagePeoplePermissions: this.$rootScope.hasPermission("ManagePeople"),
      nodeMargin: { x: 60, y: 10 },
      linkStartOffset: { x: 24, y: 31 },
      linkEndOffset: { x: 2, y: 31 },
      maxTooltipLength: 56,
      showRoot: false,
      showSomeMembers: this.$scope.showSomeTeamMembers,
      someMembersLimit: 5,
      zoom: this.defaultZoom,
      rootPosition: this.defaultTreePosition,
      zoomMax: 1.2,
      zoomMin: 0.1,
    });
  }

  private renderTreeDelayed(delay: number) {
    if (delay > 0) {
      window.setTimeoutOutsideAngular(() => this.tree.render(), this.animationDuration);
    } else {
      // this is faster than doing: setTimeout(() => this.tree.render(), 0);
      this.tree.render();
    }
  }

  private displayTree(): void {
    // set teams data
    this.tree.setNodeData(createTeamsTree(this.$scope.teams, this.$scope.teamsIdMap));

    if (this.initialDisplay && this.$scope.teams.length > 0) {
      this.onInitialDisplay();
    }

    // restore tree state
    try {
      this.tree.loadState();
      this.$scope.dragEnabled = this.tree.getOptions().isNodeDraggingEnabled;
    } catch (e) {
      console.warn(`${e.message}\n${e.stack}`);
    }

    this.apmSpanService.renderSpan("tree-render", () => {
      this.tree.render();
    });

    if (this.debug && this.pageTiming > 0) {
      // eslint-disable-next-line no-console
      console.log(`Page loading time: ${Date.now() - this.pageTiming} ms.`);
      this.pageTiming = 0;
    }
  }

  private safeDisplayTree(): void {
    try {
      this.displayTree();
    } catch (e) {
      this.uiErrorHandlingService.handleModal(e);
    }
  }

  private resetTreeView = (): void => {
    const el = document.querySelector(".tree-renderer-drawing-container") as HTMLElement;
    if (el) {
      el.classList.add("animate-reset-zoom-position-tree");
      el.style.setProperty("--reset-animation-duration", `${this.resetTreeAnimationDuration}ms`);
      el.style.setProperty("--default-position-x", `${this.defaultTreePosition.x}px`);
      el.style.setProperty("--default-position-y", `${this.defaultTreePosition.y}px`);
      el.style.setProperty("--default-zoom", `${this.defaultZoom}`);
    }

    window.setTimeoutOutsideAngular(() => {
      if (el && el.classList.contains("animate-reset-zoom-position-tree")) {
        el.classList.remove("animate-reset-zoom-position-tree");
      }

      this.tree.setOptions({
        zoom: this.defaultZoom,
        rootPosition: this.defaultTreePosition,
      });

      this.tree.render();
    }, this.resetTreeAnimationDuration);
  };
}
